// src/pages/PaymentTerms.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useTranslation,
} from "react-i18next";

import Select from "react-select";

import axios from "../axiosConfig";

import {
  customSelectStyles,
} from "../styles/reactSelectStyles";

import {
  useCompanySettingsData,
} from "../hooks/useCompanySettingsData";

import "../styles/PaymentTerms.css";

const DUE_DAY_VALUES = [
  1,
  2,
  3,
  5,
  10,
  15,
  30,
  45,
  60,
  90,
];

const multiInlineScrollStyles = {
  ...customSelectStyles,

  control: (
    base,
    state
  ) => ({
    ...(customSelectStyles.control
      ? customSelectStyles.control(
          base,
          state
        )
      : base),

    minHeight:
      44,

    height:
      44,

    overflow:
      "hidden",
  }),

  valueContainer: (
    base
  ) => ({
    ...(customSelectStyles.valueContainer
      ? customSelectStyles.valueContainer(
          base
        )
      : base),

    flexWrap:
      "nowrap",

    overflowX:
      "auto",

    overflowY:
      "hidden",

    whiteSpace:
      "nowrap",

    alignItems:
      "center",

    paddingRight:
      72,

    gap:
      6,
  }),

  multiValue: (
    base
  ) => ({
    ...(customSelectStyles.multiValue
      ? customSelectStyles.multiValue(
          base
        )
      : base),

    flex:
      "0 0 auto",

    maxWidth:
      "none",

    margin:
      0,
  }),

  multiValueLabel: (
    base
  ) => ({
    ...(customSelectStyles.multiValueLabel
      ? customSelectStyles.multiValueLabel(
          base
        )
      : base),

    maxWidth:
      140,

    overflow:
      "hidden",

    textOverflow:
      "ellipsis",

    whiteSpace:
      "nowrap",
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...(customSelectStyles.indicatorsContainer
      ? customSelectStyles.indicatorsContainer(
          base
        )
      : base),

    alignItems:
      "center",
  }),
};

const normalizeValuesArray = (
  value
) => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return [];
  }

  if (
    Array.isArray(value)
  ) {
    return value;
  }

  if (
    typeof value ===
    "string"
  ) {
    const trimmed =
      value.trim();

    if (
      trimmed.startsWith(
        "["
      )
    ) {
      try {
        const parsed =
          JSON.parse(
            trimmed
          );

        if (
          Array.isArray(
            parsed
          )
        ) {
          return parsed;
        }
      } catch {
        // fallback abaixo
      }
    }

    return trimmed
      .split(",")
      .map(
        (
          item
        ) =>
          item.trim()
      )
      .filter(Boolean);
  }

  return [];
};

const buildPaymentTermsForm = (
  details,
  dueOptions,
  paymentOptions,
  invoiceOptions
) => {
  if (!details) {
    return {
      dueDate: null,
      discountTerms: "",
      latePaymentPenalties: "",
      acceptedPaymentMethods: [],
      invoiceDeliveryMethods: [],
    };
  }

  const net =
    details.payment_net_days ??
    details.paymentNetDays ??
    30;

  const dueDate =
    dueOptions.find(
      (
        option
      ) =>
        option.value ===
        Number(net)
    ) ||
    dueOptions.find(
      (
        option
      ) =>
        option.value ===
        30
    ) ||
    null;

  const acceptedRaw =
    details.acceptedPaymentMethods ??
    details.accepted_payment_methods ??
    [];

  const acceptedPaymentMethods =
    normalizeValuesArray(
      acceptedRaw
    )
      .map(
        (
          value
        ) =>
          paymentOptions.find(
            (
              option
            ) =>
              option.value ===
              value
          )
      )
      .filter(Boolean);

  const invoiceRaw =
    details.invoiceDeliveryMethods ??
    details.invoice_delivery_methods ??
    details.invoiceDeliveryMethod ??
    details.invoice_delivery_method ??
    [];

  const invoiceDeliveryMethods =
    normalizeValuesArray(
      invoiceRaw
    )
      .map(
        (
          value
        ) =>
          invoiceOptions.find(
            (
              option
            ) =>
              option.value ===
              value
          )
      )
      .filter(Boolean);

  return {
    dueDate,

    discountTerms:
      details.discountTerms ??
      details.discount_terms ??
      "",

    latePaymentPenalties:
      details.latePaymentPenalties ??
      details.late_payment_penalties ??
      "",

    acceptedPaymentMethods,

    invoiceDeliveryMethods,
  };
};

export default function PaymentTerms({
  embedded = false,
  onSaved,
}) {
  const { t } =
    useTranslation(
      "payment_terms"
    );

  const {
    data,
    refresh,
    patchSection,
  } =
    useCompanySettingsData();

  const details =
    data?.details ||
    null;

  const dirtyRef =
    useRef(false);

  const DUE_DATE_OPTIONS =
    useMemo(
      () =>
        DUE_DAY_VALUES.map(
          (
            number
          ) => ({
            value:
              number,

            label:
              t(
                "options.dueDate.net_days",
                {
                  count:
                    number,
                }
              ),
          })
        ),
      [
        t,
      ]
    );

  const PAYMENT_METHOD_OPTIONS =
    useMemo(
      () => [
        {
          value:
            "bank_wire",

          label:
            t(
              "options.paymentMethods.bank_wire"
            ),
        },

        {
          value:
            "checks",

          label:
            t(
              "options.paymentMethods.checks"
            ),
        },

        {
          value:
            "cash",

          label:
            t(
              "options.paymentMethods.cash"
            ),
        },

        {
          value:
            "credit_debit_card",

          label:
            t(
              "options.paymentMethods.credit_debit_card"
            ),
        },

        {
          value:
            "crypto",

          label:
            t(
              "options.paymentMethods.crypto"
            ),
        },
      ],
      [
        t,
      ]
    );

  const INVOICE_DELIVERY_OPTIONS =
    useMemo(
      () => [
        {
          value:
            "email",

          label:
            t(
              "options.deliveryMethods.email"
            ),
        },

        {
          value:
            "in_person",

          label:
            t(
              "options.deliveryMethods.hand"
            ),
        },

        {
          value:
            "postal_mail",

          label:
            t(
              "options.deliveryMethods.postal_mail"
            ),
        },
      ],
      [
        t,
      ]
    );

  const [
    companyDetailsId,
    setCompanyDetailsId,
  ] = useState(
    details?.id ??
      null
  );

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    notice,
    setNotice,
  ] = useState(null);

  const [
    form,
    setForm,
  ] = useState(
    () =>
      buildPaymentTermsForm(
        details,
        DUE_DATE_OPTIONS,
        PAYMENT_METHOD_OPTIONS,
        INVOICE_DELIVERY_OPTIONS
      )
  );

  useEffect(() => {
    if (
      !details ||
      dirtyRef.current
    ) {
      return;
    }

    setCompanyDetailsId(
      details.id ??
        null
    );

    setForm(
      buildPaymentTermsForm(
        details,
        DUE_DATE_OPTIONS,
        PAYMENT_METHOD_OPTIONS,
        INVOICE_DELIVERY_OPTIONS
      )
    );
  }, [
    details,
    DUE_DATE_OPTIONS,
    PAYMENT_METHOD_OPTIONS,
    INVOICE_DELIVERY_OPTIONS,
  ]);

  const setField = (
    key,
    value
  ) => {
    dirtyRef.current =
      true;

    setForm(
      (
        previous
      ) => ({
        ...previous,

        [key]:
          value,
      })
    );
  };

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      const invoiceDeliveryMethod =
        form.invoiceDeliveryMethods?.[0]
          ?.value;

      const payload = {
        payment_net_days:
          form.dueDate
            ?.value ??
          null,

        discountTerms:
          form.discountTerms ||
          null,

        latePaymentPenalties:
          form.latePaymentPenalties ||
          null,

        acceptedPaymentMethods:
          form.acceptedPaymentMethods.map(
            (
              method
            ) =>
              method.value
          ),

        ...(invoiceDeliveryMethod
          ? {
              invoiceDeliveryMethod,
            }
          : {}),
      };

      setSaving(
        true
      );

      setNotice(
        null
      );

      try {
        if (
          !companyDetailsId
        ) {
          throw new Error(
            t(
              "errors.missing_company_id"
            )
          );
        }

        await axios.put(
          `/company-details/${companyDetailsId}`,
          payload
        );

        dirtyRef.current =
          false;

        patchSection(
          "details",
          payload
        );

        if (
          typeof onSaved ===
          "function"
        ) {
          await onSaved();
        } else {
          await refresh();
        }

        setNotice({
          type:
            "success",

          text:
            t(
              "toasts.saved_success"
            ),
        });
      } catch (error) {
        console.error(
          "[PaymentTerms] save failed",
          error
        );

        setNotice({
          type:
            "error",

          text:
            error?.response
              ?.data
              ?.error ||
            t(
              "toasts.saved_error"
            ),
        });
      } finally {
        setSaving(
          false
        );

        window.setTimeout(
          () =>
            setNotice(
              null
            ),
          3000
        );
      }
    };

  return (
    <div
      className={`payment-terms ${
        embedded
          ? "payment-terms--embedded"
          : ""
      }`}
    >
      <div className="payment-terms__header">
        <h1 className="payment-terms__title">
          {t(
            "title"
          )}
        </h1>

        <p className="payment-terms__subtitle">
          {t(
            "subtitle"
          )}
        </p>
      </div>

      {notice && (
        <div
          className={`payment-terms__notice ${
            notice.type ===
            "success"
              ? "payment-terms__notice--success"
              : "payment-terms__notice--error"
          }`}
          role="status"
          aria-live="polite"
        >
          {
            notice.text
          }
        </div>
      )}

      <form
        className="payment-terms__form"
        onSubmit={
          handleSubmit
        }
      >
        <div className="payment-terms__grid">
          <div className="payment-terms__row3">
            <div className="payment-terms__field">
              <label className="payment-terms__label">
                {t(
                  "fields.dueDate.label"
                )}
              </label>

              <Select
                options={
                  DUE_DATE_OPTIONS
                }
                value={
                  form.dueDate
                }
                onChange={(
                  value
                ) =>
                  setField(
                    "dueDate",
                    value
                  )
                }
                classNamePrefix="payment-terms-select"
                placeholder={t(
                  "fields.dueDate.placeholder"
                )}
                styles={
                  customSelectStyles
                }
                isSearchable={
                  false
                }
              />
            </div>

            <div className="payment-terms__field">
              <label className="payment-terms__label">
                {t(
                  "fields.acceptedPaymentMethods.label"
                )}
              </label>

              <Select
                isMulti
                options={
                  PAYMENT_METHOD_OPTIONS
                }
                value={
                  form.acceptedPaymentMethods
                }
                onChange={(
                  selected
                ) =>
                  setField(
                    "acceptedPaymentMethods",
                    selected ||
                      []
                  )
                }
                classNamePrefix="payment-terms-select"
                placeholder={t(
                  "fields.acceptedPaymentMethods.placeholder"
                )}
                styles={
                  multiInlineScrollStyles
                }
                closeMenuOnSelect={
                  false
                }
                hideSelectedOptions={
                  false
                }
                isSearchable={
                  false
                }
              />
            </div>

            <div className="payment-terms__field">
              <label className="payment-terms__label">
                {t(
                  "fields.invoiceDeliveryMethods.label"
                )}
              </label>

              <Select
                isMulti
                options={
                  INVOICE_DELIVERY_OPTIONS
                }
                value={
                  form.invoiceDeliveryMethods
                }
                onChange={(
                  selected
                ) =>
                  setField(
                    "invoiceDeliveryMethods",
                    selected ||
                      []
                  )
                }
                classNamePrefix="payment-terms-select"
                placeholder={t(
                  "fields.invoiceDeliveryMethods.placeholder"
                )}
                styles={
                  multiInlineScrollStyles
                }
                closeMenuOnSelect={
                  false
                }
                hideSelectedOptions={
                  false
                }
                isSearchable={
                  false
                }
              />
            </div>
          </div>

          <div className="payment-terms__field">
            <label className="payment-terms__label">
              {t(
                "fields.discountTerms.label"
              )}
            </label>

            <textarea
              className="payment-terms__textarea"
              rows={3}
              value={
                form.discountTerms
              }
              onChange={(
                event
              ) =>
                setField(
                  "discountTerms",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.discountTerms.placeholder"
              )}
            />
          </div>

          <div className="payment-terms__field">
            <label className="payment-terms__label">
              {t(
                "fields.latePaymentPenalties.label"
              )}
            </label>

            <textarea
              className="payment-terms__textarea"
              rows={3}
              value={
                form.latePaymentPenalties
              }
              onChange={(
                event
              ) =>
                setField(
                  "latePaymentPenalties",
                  event.target.value
                )
              }
              placeholder={t(
                "fields.latePaymentPenalties.placeholder"
              )}
            />
          </div>
        </div>

        <div className="payment-terms__actions">
          <button
            type="button"
            className="payment-terms__btn payment-terms__btn--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {t(
              "actions.cancel"
            )}
          </button>

          <button
            type="submit"
            className="payment-terms__btn payment-terms__btn--primary"
            disabled={
              saving
            }
          >
            {saving
              ? t(
                  "actions.saving"
                )
              : t(
                  "actions.save"
                )}
          </button>
        </div>
      </form>
    </div>
  );
}